//#region IMPORT
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
/** Services Import */
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { useDispatch, useSelector } from 'react-redux';
/** Components */
import { SER_BASIC_01 } from './index';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { showLoading, closeLoading } from '../../../../Reducer/LoadingReducer';
import { useSave } from '../../../../hooks/useSaveN';
import {
  useSearch1,
  useSearch2,
  useSave1,
  useSave2,
  useSave3,
} from './SER_BASIC_01_QUERY';
import { SER_BASIC_01_Schema } from './SER_BASIC_01_Schema';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'SeriousAccidentAct1';

//#region 상수
const Steps = [
  {
    label: '경영자 안전보건경영활동',
  },
  {
    label: '위험성 평가',
  },
  {
    label: '안전보건교육',
  },
  {
    label: '근로자 참여',
  },
  {
    label: '중대재해 대응조치',
  },
  {
    label: '도급.용역.위탁 안전보건확보',
  },
  {
    label: '평가 및 개선',
  },
  {
    label: '안전문화 및 안전의식',
  },
  {
    label: '가점 사항',
  },
];
//#endregion

function SER_BASIC_01F() {
  //#region 변수
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, lanInfo, custInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const dispatch = useDispatch();
  const custCd = location?.state?.custCd || '';

  /** 엑셀용 사업장명 (라우트 state → 검색조건 custList → BaseInfo custInfo) */
  const custNmForExcel = useMemo(() => {
    const fromNav = String(location?.state?.custNm ?? '').trim();
    if (fromNav) return fromNav;
    const cd = String(custCd || searchInfo?.custCd || '').trim();
    if (!cd) return '';
    const list = Array.isArray(searchInfo?.custList) ? searchInfo.custList : [];
    const fromList = list.find((c) => String(c.value) === cd);
    if (fromList?.name) return String(fromList.name).trim();
    const fromInfo = Array.isArray(custInfo)
      ? custInfo.find((c) => String(c.custCd) === cd)
      : null;
    if (fromInfo?.custNm) return String(fromInfo.custNm).trim();
    return '';
  }, [
    location?.state?.custNm,
    custCd,
    searchInfo?.custCd,
    searchInfo?.custList,
    custInfo,
  ]);
  const sdCodeRef = useRef(location?.state?.data?.sdCode || '');
  const [dateValue, setDateValue] = useState({
    evaDate: location?.state?.data?.evaDate
      ? location.state.data.evaDate
      : dayjs().format('YYYY-MM-DD'),
    evaName: location?.state?.data?.evaName
      ? location.state.data.evaName
      : userInfo.userNm,
  }); //조회조건 평가일자, 평가자
  const [codeList, setCodeList] = useState([]); //공통코드 데이터
  const [arrayList, setArrayList] = useState([]); //테이블 평가 지표 데이터
  const [arrayList2, setArrayList2] = useState([]); //테이블 착안사항 평가(적정) 데이터
  // 상세(착안사항) 원본 스냅샷: 저장 시 변경분만 전송하기 위함
  const arrayList2OriginRef = useRef([]);
  const [modalToggle1, setModalToggle1] = React.useState(false); //그리드 안 현장 설정하기 팝업 창
  const [imageFile, setImageFile] = React.useState({
    //이미지 설정
    mapFiles: '', //파일 String
    mapFileName: '', //파일이름
  });

  //페이지별 취득점수 총합
  const pagePoint = React.useRef({
    no1: 0,
    no2: 0,
    no3: 0,
    no4: 0,
    no5: 0,
    no6: 0,
    no7: 0,
    no8: 0,
    no9: 0,
  });

  const [totalPoint, setTotalPoint] = useState(0); //총취득점수

  const [activeStep, setActiveStep] = React.useState(0); //페이지 이동 변수
  const maxSteps = Steps.length;
  //#endregion

  // QUERY: 조회/저장 훅 연결
  const search1 = useSearch1();
  const search2 = useSearch2();
  const { runAsync: SaveMasterAsync } = useSave1();
  const { runAsync: SaveDetailAsync } = useSave2();
  const { runAsync: SavePointAsync } = useSave3();

  const initOnceRef = useRef(false);
  // 초기 1회 실행 의도 (deps 고정)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;
    (async () => {
      try {
        // 코드 목록은 항상 로딩(템플릿/계산에 필요)
        await SearchCode('0');

        // sdCode가 있으면 기존 데이터 조회
        if (sdCodeRef.current) {
          SearchLogic1(sdCodeRef.current);
        } else {
          // 신규 작성: 점수 초기화
          setTotalPoint(0);
          setActiveStep(0);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //#region 조회
  // 마스터 조회(sdCode 기준)
  const SearchLogic1 = async (sdCode) => {
    if (!sdCode) return;

    search1.run(
      { sdCode },
      {
        onSuccess: (rows) => {
          const row = rows?.[0];
          if (!row) return;

          // 화면 헤더 반영
          setDateValue((prev) => ({
            ...prev,
            evaDate: row.evaDate || prev.evaDate,
            evaName: row.evaName || prev.evaName,
          }));
          setTotalPoint(row.evaPoint || 0);
          sdCodeRef.current = row.sdCode || sdCode;

          SearchLogic2(sdCodeRef.current);
          setActiveStep(0);
        },
      }
    );
  };
  //조회버튼(_value1 : 중대재해처벌법 코드 )
  // 상세 조회(sdCode 기준)
  const SearchLogic2 = async (sdCode) => {
    if (!sdCode) return;

    // 페이지별 점수 초기화(재조회 시 누적 방지)
    Object.keys(pagePoint.current).forEach((key) => {
      pagePoint.current[key] = 0;
    });

    search2.run(
      { sdCode },
      {
        onSuccess: (data) => {
          if (!data?.length) return;

          const groupedData = data.reduce((acc, item) => {
            const key = item.codeNm;
            if (!acc[key]) {
              acc[key] = {
                codeNm: key,
                val1: item.val1,
                val2: item.val2,
                code: item.groupCd,
                val3: 0,
              };
            }
            return acc;
          }, {});

          const groupedArray = Object.values(groupedData);
          setArrayList(groupedArray);

          let changeData = [];
          for (let i = 0; i < groupedArray.length; i++) {
            const rowLen = data.filter(
              (e) => e.groupCd === groupedArray[i].code
            ).length;
            const quesPo = groupedArray[i].val2 / rowLen;
            let totalPo = 0;

            data
              .filter((e) => e.groupCd === groupedArray[i].code)
              .forEach((_item2) => {
                totalPo = _item2.checkYn !== 'N' ? totalPo + quesPo : totalPo;
                changeData.push({
                  code: _item2.seriousDisaster,
                  groupCd: _item2.groupCd,
                  codeNm: _item2.seriousDisasterNm,
                  check: _item2.checkYn || '0',
                  val1: _item2.val1,
                });
              });
            groupedArray[i].val3 = totalPo.toFixed(2);
            ChangePagePoint(groupedArray[i].val1, totalPo, 'add');
          }

          setArrayList2(changeData);
          arrayList2OriginRef.current = (changeData || []).map((r) => ({
            ...r,
          }));
          SearchCode('1');
        },
      }
    );
  };
  //점수표
  const SearchLogic3 = async () => {
    try {
      await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Execute.ftpResponse,
        {
          value1: Commons.EncryptAES('/Serious/점수표.png'), // 보건검진사진 url
        }
      ).then((result) => {
        let data = result.data;
        if (data !== 'Error') {
          setImageFile({
            ...imageFile,
            mapFiles: `data:image/png;base64,${data}`,
            mapFileName: '점수표.png',
          });
        } else {
          setImageFile({
            mapFiles: '',
            mapFileName: '점수표.png',
          });
        }

        setModalToggle1(true);
      });
    } catch (e) {
      console.log(e);
    } finally {
    }
  };
  // 조회(_value1 : "0"이면 데이터없을경우 "1"이면 데이터있을경우)
  const SearchCode = async (_value1) => {
    try {
      const result = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Utility.getCodeInfo,
        {}
      );

      const apiRes = result?.data;
      const isWrapped =
        apiRes && typeof apiRes === 'object' && 'data' in apiRes;

      // 1) 에러 처리(Wrapper 응답 우선)
      if (isWrapped && apiRes?.isSuccess === false) {
        dispatch(
          showSnackbar({
            message: apiRes?.message || '코드 정보를 불러오지 못했습니다.',
            type: apiRes?.severity || 'error',
          })
        );
        setCodeList([]);
        return false;
      }

      // 2) rows 추출(Wrapper/Legacy 모두 대응)
      const rows = Array.isArray(apiRes)
        ? apiRes
        : Array.isArray(apiRes?.data)
          ? apiRes.data
          : [];

      if (rows.length === 0) {
        setCodeList([]);
        return;
      }

      // Legacy 스타일(type: 'Error') 방어
      if (rows?.[0]?.type === 'Error') {
        dispatch(showSnackbar({ message: rows?.[0]?.message, type: 'error' }));
        setCodeList([]);
        return false;
      }

      // 3) 필드명 표준화(GetCodeInfo: GroupCd/Code/... → 기존 화면: groupCd/code/...)
      const normalized = rows.map((r) => ({
        groupCd: r?.groupCd ?? r?.GroupCd ?? '',
        code: r?.code ?? r?.Code ?? '',
        codeNm: r?.codeNm ?? r?.CodeNm ?? '',
        val1: r?.val1 ?? r?.Val1 ?? '',
        val2: r?.val2 ?? r?.Val2 ?? '',
        val3: r?.val3 ?? r?.Val3 ?? '',
        ref1: r?.ref1 ?? r?.Ref1 ?? '',
        ref2: r?.ref2 ?? r?.Ref2 ?? '',
        ref3: r?.ref3 ?? r?.Ref3 ?? '',
      }));

      setCodeList(normalized);

      // NOTE:
      // - 착안사항(상세) 저장/조회가 "변경분만" 기준이면 DB에 없는 항목은 조회에서 빠져 라디오가 비어보일 수 있음
      // - 따라서 화면에서는 항상 "템플릿(전체 항목)"을 구성하고, 기존 데이터가 있으면 check 값을 merge 한다.
      const checkMap = new Map(
        (arrayList2OriginRef.current || []).map((r) => [r.code, r.check || '0'])
      );
      const defaultCheck = 'N'; // 기존 UX: 신규 평가는 미흡(N) 기본 선택

      const seriousData = normalized.filter((e) => e.groupCd === 'SERIOUS_DISASTER');
      const nextArrayList = [];
      const nextArrayList2 = [];

      seriousData.forEach((header) => {
        const details = normalized.filter((e) => e.groupCd === header.code);
        const rowLen = details.length || 1;
        const quesPo = Number(header.val2 || 0) / rowLen;

        let totalPo = 0;
        details.forEach((d) => {
          const mergedCheck =
            (_value1 === '1' ? checkMap.get(d.code) : null) ?? defaultCheck;

          // 점수 계산: 기존 로직 유지 (N=미흡은 점수 0, Y/0는 점수 반영)
          if (mergedCheck === 'Y' || mergedCheck === '0') {
            totalPo += quesPo;
          }

          nextArrayList2.push({
            code: d.code,
            groupCd: d.groupCd,
            codeNm: d.codeNm,
            check: mergedCheck,
            val1: d.val1,
          });
        });

        nextArrayList.push({
          code: header.code,
          codeNm: header.codeNm,
          val1: header.val1,
          val2: header.val2,
          val3: totalPo.toFixed(2), //취득점수(표시용)
        });
      });

      setArrayList(nextArrayList);
      setArrayList2(nextArrayList2);
      // 원본 스냅샷도 "전체 항목" 기준으로 유지(저장/merge에 사용)
      arrayList2OriginRef.current = (nextArrayList2 || []).map((r) => ({ ...r }));
    } catch (e) {
      console.log(e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
    }
  };
  //#endregion
  //#region 저장
  //저장버튼 클릭 시 함수()
  const { run: SaveAll } = useSave({
    menuId: SCREEN_MENU_ID,
    schema: SER_BASIC_01_Schema,
    getChangedRows: () => [
      {
        sdCode: sdCodeRef.current || '',
        custCd,
        evaDate: dayjs(dateValue.evaDate).format('YYYY-MM-DD'),
        evaName: dateValue.evaName,
        evaPoint: Math.round(Number(totalPoint) || 0),
      },
    ],
    requestSave: async (rows) => {
      const header = rows?.[0] || {};
      const wasNew = !sdCodeRef.current;

      // 1) 마스터 저장
      const res1 = await SaveMasterAsync([header]);
      const isSuccess1 = res1?.isSuccess ?? res1?.IsSuccess;
      if (isSuccess1 === false) {
        throw new Error(
          res1?.message || res1?.Message || '저장 중 오류가 발생했습니다.'
        );
      }
      const d1 = Array.isArray(res1?.data) ? res1.data : [];
      const newSdCode =
        d1?.[0]?.sdCode ||
        d1?.[0]?.SdCode ||
        header.sdCode ||
        sdCodeRef.current;
      sdCodeRef.current = newSdCode;

      // 2) 상세 저장(착안사항 평가)
      // 요구사항: 변경 여부와 무관하게 "전체 착안사항"을 저장해야 함
      // (그래야 다음 조회 때 라디오가 전부 체크된 상태로 내려옴)
      const allDetailRows = (arrayList2 || []).map((r) => ({
        sdCode: newSdCode,
        seriousDisaster: r.code,
        checkYn: r.check || '0',
      }));

      const res2 =
        allDetailRows.length > 0
          ? await SaveDetailAsync(allDetailRows)
          : { severity: 'success', data: [] };
      const d2 = Array.isArray(res2?.data) ? res2.data : [];

      // 3) 페이지별 점수 저장
      const evaDateStr = dayjs(dateValue.evaDate).format('YYYY-MM-DD');
      const pointRows = Steps.map((s, idx) => ({
        value: pagePoint.current[`no${idx + 1}`] ?? 0,
        codeNm: s.label,
        custCd,
        evaDate: evaDateStr,
      }));
      const res3 = await SavePointAsync(pointRows);
      const d3 = Array.isArray(res3?.data) ? res3.data : [];

      // requestSave의 반환값은 useSaveN 내부에서 스낵바(detailData)에 사용됨
      // - useSaveN은 isSuccess 기반으로 성공/실패 집계하므로, detailData는 isSuccess 포함 형태로 통일
      const merged = [
        {
          isSuccess: true,
          statusType: res1?.statusType || res1?.StatusType,
          message: res1?.message || res1?.Message,
          sdCode: newSdCode,
          type: 'MASTER',
        },
        ...d2,
        ...d3,
      ];

      // 신규 저장 후에는 목록으로 이동(기존 UX 유지)
      if (wasNew) {
        navigate('/SeriousAccidentAct');
      } else {
        SearchLogic1(newSdCode);
      }

      // 저장 성공 후 원본 스냅샷 갱신(다음 저장부터 변경분만 전송)
      arrayList2OriginRef.current = (arrayList2 || []).map((r) => ({ ...r }));

      return { ...res1, severity: 'success', data: merged };
    },
  });

  const SaveLogic1 = async () => {
    await SaveAll();
  };
  //#endregion
  //#region 삭제

  //#endregion
  //#region 나머지 함수
  const HandleNext = () => {
    // if (maxSteps === activeStep + 1) {
    //   SaveLogic1();
    // } else {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // }
  };

  const HandleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  //페이지별 취득점수
  const ChangePagePoint = (_value1, _value2, _value3) => {
    if (_value3 === 'add') {
      switch (_value1) {
        case '1':
          pagePoint.current.no1 = pagePoint.current.no1 + _value2;
          break;
        case '2':
          pagePoint.current.no2 = pagePoint.current.no2 + _value2;
          break;
        case '3':
          pagePoint.current.no3 = pagePoint.current.no3 + _value2;
          break;
        case '4':
          pagePoint.current.no4 = pagePoint.current.no4 + _value2;
          break;
        case '5':
          pagePoint.current.no5 = pagePoint.current.no5 + _value2;
          break;
        case '6':
          pagePoint.current.no6 = pagePoint.current.no6 + _value2;
          break;
        case '7':
          pagePoint.current.no7 = pagePoint.current.no7 + _value2;
          break;
        case '8':
          pagePoint.current.no8 = pagePoint.current.no8 + _value2;
          break;
        case '9':
          pagePoint.current.no9 = pagePoint.current.no9 + _value2;
          break;
        default:
          break;
      }
    } else {
      switch (_value1) {
        case '1':
          pagePoint.current.no1 = pagePoint.current.no1 - _value2;
          break;
        case '2':
          pagePoint.current.no2 = pagePoint.current.no2 - _value2;
          break;
        case '3':
          pagePoint.current.no3 = pagePoint.current.no3 - _value2;
          break;
        case '4':
          pagePoint.current.no4 = pagePoint.current.no4 - _value2;
          break;
        case '5':
          pagePoint.current.no5 = pagePoint.current.no5 - _value2;
          break;
        case '6':
          pagePoint.current.no6 = pagePoint.current.no6 - _value2;
          break;
        case '7':
          pagePoint.current.no7 = pagePoint.current.no7 - _value2;
          break;
        case '8':
          pagePoint.current.no8 = pagePoint.current.no8 - _value2;
          break;
        case '9':
          pagePoint.current.no9 = pagePoint.current.no9 - _value2;
          break;
        default:
          break;
      }
    }
  };

  // 라디오버튼 클릭시 이벤트 (_value1 : 평가(적정) 데이터 , _value2 : 라디오값,_value3:취득점수 데이터)
  const HandleCheckboxChange = (_value1, _value2, _value3) => {
    //체크박스 질문 갯수
    let rowLen = codeList.filter((e) => e.groupCd === _value1.groupCd).length;
    let a = Number(_value3.val2);
    let quesPo = a / rowLen; //질문 개당 점수
    let totalPo = Number(_value3.val3); //테이블안 취득점수 변수

    // 이전 체크 값 가져오기
    const prevCheck = arrayList2.find(
      (item) => item.code === _value1.code
    )?.check;

    // 이전 값과 새로운 값이 다른 경우에만 점수 계산
    if (prevCheck !== _value2) {
      // 이전 값이 Y나 0이었다면 점수 빼기
      if (prevCheck === 'Y' || prevCheck === '0') {
        totalPo = (totalPo - quesPo).toFixed(2);
        setTotalPoint((prev) => (Number(prev) - quesPo).toFixed(2));
        ChangePagePoint(_value1.val1, quesPo, 'sub');
      }

      // 새로운 값이 Y나 0이면 점수 더하기
      if (_value2 === 'Y' || _value2 === '0') {
        totalPo = (Number(totalPo) + quesPo).toFixed(2);
        setTotalPoint((prev) => (Number(prev) + quesPo).toFixed(2));
        ChangePagePoint(_value1.val1, quesPo, 'add');
      }
    }

    //[코드] : 체크여부
    setArrayList2((prevList) =>
      prevList.map((item) =>
        item.code === _value1.code ? { ...item, check: _value2 } : item
      )
    );

    //취득점수 변경
    setArrayList((prevList) =>
      prevList.map((item) =>
        item.code === _value3.code ? { ...item, val3: totalPo } : item
      )
    );
  };

  /** 평가 라디오 값 → 표시문구 */
  const FormatSerEvalCheck = (check) => {
    if (check === 'Y') return '양호';
    if (check === 'N') return '미흡';
    return '해당없음';
  };

  /** Excel 시트명 금지문자 제거·길이 제한(31자) */
  const SanitizeExcelSheetName = (name, maxLen = 31) => {
    const cleaned = String(name ?? '')
      .replace(/[:\\/?*[\]]/g, '_')
      .trim();
    const base = cleaned || 'Sheet';
    return base.length > maxLen ? base.slice(0, maxLen) : base;
  };

  /**
   * 중대재해 평가 한 단계(val1)를 워크시트에 기록 (1행~요약, 2행 사업장명, 3행 헤더, 4행~ 데이터)
   */
  const WriteSeriousAccidentStepToWorksheet = (ws, stepVal) => {
    const stepValStr = String(stepVal);
    ws.views = [{ state: 'frozen', ySplit: 3 }];

    const evaDateRaw = dateValue.evaDate;
    const evaDateOnly =
      evaDateRaw != null && String(evaDateRaw).trim() !== ''
        ? dayjs(evaDateRaw).isValid()
          ? dayjs(evaDateRaw).format('YYYY-MM-DD')
          : String(evaDateRaw).split(/[T ]/)[0].slice(0, 10)
        : '';

    ws.getRow(1).values = [
      '평가일자',
      evaDateOnly,
      '평가자',
      dateValue.evaName || '',
      '총 취득 점수',
      Math.round(Number(totalPoint) || 0),
    ];
    ws.getRow(2).values = [
      '사업장명',
      custNmForExcel || '-',
      '',
      '',
      '',
      '',
    ];

    const headerRow = ws.getRow(3);
    headerRow.values = ['소계', '평가지표', '착안사항', '배점', '평가', '취득점수'];
    headerRow.font = { name: '맑은 고딕', size: 11, bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    for (let c = 1; c <= 6; c += 1) {
      const cell = headerRow.getCell(c);
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF808080' } },
        left: { style: 'thin', color: { argb: 'FF808080' } },
        bottom: { style: 'thin', color: { argb: 'FF808080' } },
        right: { style: 'thin', color: { argb: 'FF808080' } },
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F7F8' },
      };
    }

    const stepRows = arrayList.filter((e) => String(e.val1) === stepValStr);
    let excelRow = 4;

    stepRows.forEach((_item, _index) => {
      const children = arrayList2.filter((e) => e.groupCd === _item.code);
      const subtotalLabel = `${_item.val1}-${_index + 1}`;
      const scoreDisplay =
        _item.val3 === '' || _item.val3 == null ? 0 : _item.val3;

      const writeDataRow = (child) => {
        const row = ws.getRow(excelRow);
        row.getCell(1).value = subtotalLabel;
        row.getCell(2).value = _item.codeNm || '';
        row.getCell(3).value = child?.codeNm || '';
        row.getCell(4).value = _item.val2 ?? '';
        row.getCell(5).value = FormatSerEvalCheck(child?.check);
        row.getCell(6).value = scoreDisplay;

        for (let c = 1; c <= 6; c += 1) {
          const cell = row.getCell(c);
          cell.font = { name: '맑은 고딕', size: 10 };
          cell.alignment = {
            vertical: 'middle',
            horizontal: c === 3 ? 'left' : 'center',
            wrapText: c === 3,
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF808080' } },
            left: { style: 'thin', color: { argb: 'FF808080' } },
            bottom: { style: 'thin', color: { argb: 'FF808080' } },
            right: { style: 'thin', color: { argb: 'FF808080' } },
          };
        }
        excelRow += 1;
      };

      if (!children.length) {
        writeDataRow(null);
      } else {
        children.forEach((child) => writeDataRow(child));
      }
    });

    if (!stepRows.length) {
      const emptyRow = ws.getRow(excelRow);
      emptyRow.getCell(1).value = '(해당 단계 평가 항목 없음)';
      emptyRow.getCell(1).font = { name: '맑은 고딕', size: 10, italic: true };
      excelRow += 1;
    }

    ws.columns = [
      { width: 10 },
      { width: 28 },
      { width: 48 },
      { width: 10 },
      { width: 12 },
      { width: 12 },
    ];
  };

  /** 경영자 안전보건경영활동(1단계) 엑셀 다운로드 */
  const ExcelDownload1 = async () => {
    try {
      dispatch(showLoading());

      const stepRows = arrayList.filter((e) => String(e.val1) === '1');
      if (!stepRows.length) {
        dispatch(
          showSnackbar({
            message: '다운로드할 평가 항목이 없습니다. 조회 후 다시 시도해 주세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet(
        SanitizeExcelSheetName(Steps[0]?.label || '1단계')
      );
      WriteSeriousAccidentStepToWorksheet(ws, '1');

      const dateTimeStr = dayjs().format('YYYYMMDD_HHmmss');
      const fileName = `경영자_안전보건경영활동_${dateTimeStr}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);

      dispatch(
        showSnackbar({
          message: '엑셀 다운로드가 완료되었습니다.',
          type: 'success',
        })
      );
      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel download error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error?.message || ''}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  /** Steps 전체(경영자~가점) 단계별 시트 일괄 엑셀 다운로드 */
  const ExcelDownloadAll = async () => {
    try {
      dispatch(showLoading());

      if (!arrayList.length) {
        dispatch(
          showSnackbar({
            message: '다운로드할 평가 데이터가 없습니다. 조회 후 다시 시도해 주세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Lingvo';

      Steps.forEach((step, idx) => {
        const stepNo = idx + 1;
        const sheetName = SanitizeExcelSheetName(`${stepNo}_${step.label}`);
        const ws = workbook.addWorksheet(sheetName);
        WriteSeriousAccidentStepToWorksheet(ws, String(stepNo));
      });

      const dateTimeStr = dayjs().format('YYYYMMDD_HHmmss');
      const fileName = `중대재해처벌법_평가_전체단계_${dateTimeStr}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);

      dispatch(
        showSnackbar({
          message: `전체 ${Steps.length}개 단계 엑셀 다운로드가 완료되었습니다.`,
          type: 'success',
        })
      );
      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel bulk download error:', error);
      dispatch(
        showSnackbar({
          message: `일괄 엑셀 다운로드 중 오류가 발생했습니다: ${error?.message || ''}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  //#endregion

  return (
    <div className="wrap">
      <SER_BASIC_01
        userInfo={userInfo}
        dateValue={dateValue}
        setDateValue={setDateValue}
        lanList={lanInfo}
        HandleNext={HandleNext}
        HandleBack={HandleBack}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        codeList={codeList}
        setCodeList={setCodeList}
        arrayList={arrayList}
        setArrayList={setArrayList}
        arrayList2={arrayList2}
        setArrayList2={setArrayList2}
        HandleCheckboxChange={HandleCheckboxChange}
        totalPoint={totalPoint}
        setTotalPoint={setTotalPoint}
        steps={Steps}
        maxSteps={maxSteps}
        navigate={navigate}
        modalToggle1={modalToggle1}
        setModalToggle1={setModalToggle1}
        imageFile={imageFile}
        setImageFile={setImageFile}
        // 함수
        SearchLogic1={() => SearchLogic1(sdCodeRef.current)}
        SearchLogic3={SearchLogic3}
        SaveLogic1={SaveLogic1}
        ExcelDownload1={ExcelDownload1}
        ExcelDownloadAll={ExcelDownloadAll}
      />
    </div>
  );
}

export default SER_BASIC_01F;
